/**
 * Chatbot API - API calls for chatbot settings
 */
import axiosClient from "./axiosClient";

const chatbotApi = {
  /**
   * Get all chatbot settings by account
   */
  getSettings: (accountId, chatbotType) => {
    const params = { accountId };
    if (chatbotType) params.chatbotType = chatbotType;
    return axiosClient.get("/chatbot-settings", { params });
  },

  /**
   * Get available event types
   */
  getEventTypes: (accountId) => {
    return axiosClient.get("/chatbot-settings/event-types", {
      params: { accountId },
    });
  },

  /**
   * Create new chatbot setting
   */
  createSetting: (data) => {
    return axiosClient.post("/chatbot-settings", data);
  },

  /**
   * Update chatbot setting
   */
  updateSetting: (id, data) => {
    return axiosClient.put(`/chatbot-settings/${id}`, data);
  },

  /**
   * Delete chatbot setting
   */
  deleteSetting: (id, accountId) => {
    return axiosClient.delete(`/chatbot-settings/${id}`, {
      params: { accountId },
    });
  },

  /**
   * Get chatbot logs by contact ID
   */
  getLogsByContact: (contactId, chatbotType, accountId) => {
    const params = {};
    if (chatbotType) params.chatbotType = chatbotType;
    if (accountId) params.accountId = accountId;
    return axiosClient.get(`/chatbot-logs/contact/${contactId}`, { params });
  },

  /**
   * Get chatbot logs by chatbot_setting_id with optional filters & pagination.
   * Used by the redesigned settings page (Tab "Tin nhắn").
   *
   * @param {string} settingId
   * @param {object} params - { status, dateFrom, dateTo, search, limit, offset }
   *   status: array of statuses or single string; serialized as comma-list.
   */
  getLogsBySetting: (settingId, params = {}) => {
    const query = {};
    if (params.status) {
      query.status = Array.isArray(params.status)
        ? params.status.join(",")
        : params.status;
    }
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.search) query.search = params.search;
    if (params.limit != null) query.limit = params.limit;
    if (params.offset != null) query.offset = params.offset;
    if (params.accountId != null) query.accountId = params.accountId;
    return axiosClient.get(`/chatbot-logs/setting/${settingId}`, {
      params: query,
    });
  },

  /**
   * Get aggregated stats per status for a chatbot_setting.
   * @param {string} settingId
   * @param {object} params - { dateFrom, dateTo }
   */
  getStatsBySetting: (settingId, params = {}) => {
    const query = {};
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.accountId != null) query.accountId = params.accountId;
    return axiosClient.get(`/chatbot-logs/setting/${settingId}/stats`, {
      params: query,
    });
  },

  /**
   * Get chatbot_log rows across the whole account, with optional per-bot filter.
   * Used by the "Toàn bộ log" view.
   *
   * @param {number|string} accountId
   * @param {object} params - { status, settingIds, dateFrom, dateTo, search,
   *                            chatbotType, limit, offset }
   */
  getLogsByAccount: (accountId, params = {}) => {
    const query = {};
    if (params.status) {
      query.status = Array.isArray(params.status)
        ? params.status.join(",")
        : params.status;
    }
    if (params.settingIds) {
      query.settingIds = Array.isArray(params.settingIds)
        ? params.settingIds.join(",")
        : params.settingIds;
    }
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.search) query.search = params.search;
    if (params.chatbotType) query.chatbotType = params.chatbotType;
    if (params.limit != null) query.limit = params.limit;
    if (params.offset != null) query.offset = params.offset;
    return axiosClient.get(`/chatbot-logs/account/${accountId}`, {
      params: query,
    });
  },
};

export default chatbotApi;
